import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Table, Badge, Button, Alert, Modal, Form } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { FaEdit, FaTrash, FaEye, FaFilter, FaSort, FaSortUp, FaSortDown, FaCheck, FaTimes } from 'react-icons/fa';
import SearchBar from '../../components/common/SearchBar';
import { deleteAdminProduct, getAdminProducts, updateAdminProduct } from '../../services/adminService';

const AdminProducts = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const categories = React.useMemo(() => {
    const unique = new Set(
      products
        .map(p => p?.category)
        .filter(c => typeof c === 'string' && c.trim().length > 0)
    );
    return Array.from(unique).sort((a, b) => a.localeCompare(b));
  }, [products]);
  
  // Filters and search
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [reportedFilter, setReportedFilter] = useState('');
  const [featuredFilter, setFeaturedFilter] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'dateAdded', direction: 'desc' });
  
  // Modal states
  const [showProductModal, setShowProductModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  
  // Fetch products data
  useEffect(() => {
    let mounted = true;

    const fetchProducts = async () => {
      setLoading(true);
      setError('');
      try {
        const data = await getAdminProducts();
        if (!mounted) return;
        setProducts(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error(err);
        if (!mounted) return;
        setError(err?.message || 'Failed to load products');
        setProducts([]);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    fetchProducts();
    return () => {
      mounted = false;
    };
  }, []);

  const getNestedValue = (obj, key) => {
    if (!obj || !key) return undefined;
    return key.split('.').reduce((acc, part) => (acc && acc[part] !== undefined ? acc[part] : undefined), obj);
  };
  
  // Handle search
  const handleSearch = (searchTerm = '') => {
    setSearchTerm(searchTerm);
    // The actual search is handled by the useEffect hook
  };

  // Handle sort
  const requestSort = (key) => {
    let direction = 'asc';
    
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    
    setSortConfig({ key, direction });
  };
  
  // Get sort icon
  const getSortIcon = (key) => {
    if (sortConfig.key !== key) {
      return <FaSort className="ms-1 text-muted" />;
    }
    
    return sortConfig.direction === 'asc' ? 
      <FaSortUp className="ms-1 text-primary" /> : 
      <FaSortDown className="ms-1 text-primary" />;
  };
  
  // Sort and filter products
  const filteredProducts = React.useMemo(() => {
    let filtered = [...products];
    
    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(product => 
        product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.seller.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    // Apply category filter
    if (categoryFilter) {
      filtered = filtered.filter(product => product.category === categoryFilter);
    }
    
    // Apply status filter
    if (statusFilter) {
      filtered = filtered.filter(product => product.status === statusFilter);
    }
    
    // Apply reported filter
    if (reportedFilter) {
      const isReported = reportedFilter === 'reported';
      filtered = filtered.filter(product => product.reported === isReported);
    }
    
    // Apply featured filter
    if (featuredFilter) {
      const isFeatured = featuredFilter === 'featured';
      filtered = filtered.filter(product => product.featured === isFeatured);
    }
    
    // Apply sorting
    return filtered.sort((a, b) => {
      const aVal = getNestedValue(a, sortConfig.key);
      const bVal = getNestedValue(b, sortConfig.key);

      if (aVal < bVal) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aVal > bVal) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [products, searchTerm, categoryFilter, statusFilter, reportedFilter, featuredFilter, sortConfig]);
  
  // Handle product deletion
  const handleDeleteProduct = async (productId) => {
    if (window.confirm('Are you sure you want to delete this product?')) {
      try {
        setError('');
        await deleteAdminProduct(productId);
        setProducts(prevProducts => prevProducts.filter(product => product.id !== productId));
        setSuccess('Product deleted successfully');
        
        // Clear success message after 3 seconds
        setTimeout(() => {
          setSuccess('');
        }, 3000);
      } catch (err) {
        console.error(err);
        setError(err?.message || 'Failed to delete product');
        
        // Clear error message after 3 seconds
        setTimeout(() => {
          setError('');
        }, 3000);
      }
    }
  };

  const handleSetApprovalStatus = async (productId, approvalStatus) => {
    try {
      setError('');
      const target = products.find(p => String(p.id) === String(productId));
      if (!target) throw new Error('Product not found');

      const updated = await updateAdminProduct(productId, { approvalStatus });
      setProducts(prev => prev.map(p => (String(p.id) === String(productId) ? updated : p)));

      setSuccess('Product approval status updated successfully');
      setTimeout(() => {
        setSuccess('');
      }, 3000);
    } catch (err) {
      console.error(err);
      setError(err?.message || 'Failed to update approval status');
      setTimeout(() => {
        setError('');
      }, 3000);
    }
  };
  
  // Handle product status toggle
  const handleToggleStatus = async (productId) => {
    try {
      setError('');
      const target = products.find(p => String(p.id) === String(productId));
      if (!target) throw new Error('Product not found');

      // Rotate through available -> suspended -> hidden -> available
      let newStatus;
      switch (target.status) {
        case 'available':
          newStatus = 'suspended';
          break;
        case 'suspended':
          newStatus = 'hidden';
          break;
        default:
          newStatus = 'available';
      }

      const updated = await updateAdminProduct(productId, { status: newStatus });
      setProducts(prevProducts => prevProducts.map(p => (String(p.id) === String(productId) ? updated : p)));
      setSuccess('Product status updated successfully');
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccess('');
      }, 3000);
    } catch (err) {
      console.error(err);
      setError(err?.message || 'Failed to update product status');
      
      // Clear error message after 3 seconds
      setTimeout(() => {
        setError('');
      }, 3000);
    }
  };
  
  // Handle featured toggle
  const handleToggleFeatured = async (productId) => {
    try {
      setError('');
      const target = products.find(p => String(p.id) === String(productId));
      if (!target) throw new Error('Product not found');

      // Backend does not currently persist featured flag; best-effort patch
      const updated = await updateAdminProduct(productId, { featured: !target.featured });
      setProducts(prev => prev.map(p => (String(p.id) === String(productId) ? updated : p)));

      setSuccess('Product featured status updated successfully');
      setTimeout(() => {
        setSuccess('');
      }, 3000);
    } catch (err) {
      console.error(err);
      setError(err?.message || 'Failed to update featured status');
      setTimeout(() => {
        setError('');
      }, 3000);
    }
  };
  
  // Handle reported toggle
  const handleClearReport = async (productId) => {
    try {
      setError('');
      const target = products.find(p => String(p.id) === String(productId));
      if (!target) throw new Error('Product not found');

      // Backend does not currently persist reported flag; best-effort patch
      const updated = await updateAdminProduct(productId, { reported: false });
      setProducts(prev => prev.map(p => (String(p.id) === String(productId) ? updated : p)));

      setSuccess('Product report cleared successfully');
      setTimeout(() => {
        setSuccess('');
      }, 3000);
    } catch (err) {
      console.error(err);
      setError(err?.message || 'Failed to clear report');
      setTimeout(() => {
        setError('');
      }, 3000);
    }
  };
  
  // Open product modal
  const handleOpenProductModal = (product) => {
    setSelectedProduct(product);
    setShowProductModal(true);
  };
  return (
    <>
      <div className="admin-page-content">
        <h2 className="mb-4">Product Management</h2>
        
        {error && <Alert variant="danger">{error}</Alert>}
        {success && <Alert variant="success">{success}</Alert>}
        
        <Card className="shadow-sm mb-4">
          <Card.Body>
            <Row className="mb-3 align-items-center">
              <Col md={6} lg={4}>
                <SearchBar 
                  placeholder="Search products..."
                  value={searchTerm}
                  onChange={setSearchTerm}
                  onSubmit={handleSearch}
                  size="sm"
                />
              </Col>
              <Col md={6} lg={8} className="mt-3 mt-md-0">
                <div className="d-flex flex-wrap gap-2">
                  <Form.Select
                    value={categoryFilter}
                    onChange={(e) => setCategoryFilter(e.target.value)}
                    className="w-auto"
                  >
                    <option value="">All Categories</option>
                    {categories.map((category, index) => (
                      <option key={index} value={category}>{category}</option>
                    ))}
                  </Form.Select>
                  
                  <Form.Select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="w-auto"
                  >
                    <option value="">All Statuses</option>
                    <option value="available">Available</option>
                    <option value="hidden">Hidden</option>
                    <option value="sold out">Sold Out</option>
                  </Form.Select>
                  
                  <Form.Select
                    value={reportedFilter}
                    onChange={(e) => setReportedFilter(e.target.value)}
                    className="w-auto"
                  >
                    <option value="">All Reports</option>
                    <option value="reported">Reported</option>
                    <option value="not_reported">Not Reported</option>
                  </Form.Select>
                  
                  <Form.Select
                    value={featuredFilter}
                    onChange={(e) => setFeaturedFilter(e.target.value)}
                    className="w-auto"
                  >
                    <option value="">All Featured</option>
                    <option value="featured">Featured</option>
                    <option value="not_featured">Not Featured</option>
                  </Form.Select>
                  
                  <Button 
                    variant="outline-secondary"
                    onClick={() => {
                      setSearchTerm('');
                      setCategoryFilter('');
                      setStatusFilter('');
                      setReportedFilter('');
                      setFeaturedFilter('');
                    }}
                  >
                    Clear Filters
                  </Button>
                </div>
              </Col>
            </Row>
            
            {loading ? (
              <div className="text-center py-5">
                <h4>Loading products...</h4>
              </div>
            ) : filteredProducts.length === 0 ? (
              <div className="text-center py-5">
                <h4>No products found</h4>
                <p className="text-muted">
                  Try adjusting your filters
                </p>
              </div>
            ) : (
              <div className="table-responsive">
                <Table hover className="align-middle">
                  <thead>
                    <tr>
                      <th 
                        onClick={() => requestSort('name')}
                        className="cursor-pointer"
                      >
                        Product {getSortIcon('name')}
                      </th>
                      <th 
                        onClick={() => requestSort('price')}
                        className="cursor-pointer"
                      >
                        Price {getSortIcon('price')}
                      </th>
                      <th 
                        onClick={() => requestSort('seller.name')}
                        className="cursor-pointer"
                      >
                        Seller {getSortIcon('seller.name')}
                      </th>
                      <th 
                        onClick={() => requestSort('stock')}
                        className="cursor-pointer"
                      >
                        Stock {getSortIcon('stock')}
                      </th>
                      <th 
                        onClick={() => requestSort('status')}
                        className="cursor-pointer"
                      >
                        Status {getSortIcon('status')}
                      </th>
                      <th 
                        onClick={() => requestSort('approvalStatus')}
                        className="cursor-pointer"
                      >
                        Approval {getSortIcon('approvalStatus')}
                      </th>
                      <th>Featured</th>
                      <th>Reported</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredProducts.map(product => (
                      <tr key={product.id}>
                        <td>
                          <div className="d-flex align-items-center">
                            <img 
                              src={product.image} 
                              alt={product.name} 
                              className="me-3"
                              width="50"
                              height="50"
                              style={{ objectFit: 'cover' }}
                            />
                            <div>
                              <div className="fw-bold">{product.name}</div>
                              <small className="text-muted">{product.category}</small>
                            </div>
                          </div>
                        </td>
                        <td>GH₵ {product.price.toFixed(2)}</td>
                        <td>
                          <div>{product.seller.name}</div>
                          <small className="text-muted">{product.seller.department}</small>
                        </td>
                        <td>{product.stock}</td>
                        <td>
                          {product.status === 'available' ? (
                            <Badge bg="success">Available</Badge>
                          ) : product.status === 'sold out' ? (
                            <Badge bg="secondary">Sold Out</Badge>
                          ) : product.status === 'suspended' ? (
                            <Badge bg="warning" text="dark">Suspended</Badge>
                          ) : (
                            <Badge bg="danger">Hidden</Badge>
                          )}
                        </td>
                        <td>
                          <div className="d-flex align-items-center gap-2 flex-wrap">
                            {product.approvalStatus === 'approved' ? (
                              <Badge bg="success">Approved</Badge>
                            ) : product.approvalStatus === 'rejected' ? (
                              <Badge bg="danger">Rejected</Badge>
                            ) : (
                              <Badge bg="warning" text="dark">Pending</Badge>
                            )}

                            {product.approvalStatus !== 'approved' && (
                              <Button
                                variant="outline-success"
                                size="sm"
                                onClick={() => handleSetApprovalStatus(product.id, 'approved')}
                              >
                                Approve
                              </Button>
                            )}
                            {product.approvalStatus !== 'rejected' && (
                              <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() => handleSetApprovalStatus(product.id, 'rejected')}
                              >
                                Reject
                              </Button>
                            )}
                          </div>
                        </td>
                        <td>
                          <Button 
                            variant={product.featured ? 'success' : 'outline-secondary'}
                            size="sm"
                            onClick={() => handleToggleFeatured(product.id)}
                          >
                            {product.featured ? <FaCheck /> : <FaTimes />}
                          </Button>
                        </td>
                        <td>
                          {product.reported ? (
                            <Button 
                              variant="danger"
                              size="sm"
                              onClick={() => handleClearReport(product.id)}
                            >
                              Clear Report
                            </Button>
                          ) : (
                            <Badge bg="success">No Reports</Badge>
                          )}
                        </td>
                        <td>
                          <div className="d-flex">
                            <Button 
                              variant="outline-primary"
                              size="sm"
                              className="me-2"
                              onClick={() => handleOpenProductModal(product)}
                              title="View Details"
                            >
                              <FaEye />
                            </Button>
                            <Button
                              variant={product.status === 'available' ? 'outline-warning' : product.status === 'suspended' ? 'outline-danger' : 'outline-success'}
                              size="sm"
                              className="me-1"
                              disabled={product.status === 'sold out'}
                              onClick={() => handleToggleStatus(product.id)}
                            >
                              {product.status === 'available' ? 'Suspend' : product.status === 'suspended' ? 'Hide' : 'Show'}
                            </Button>
                            <Button 
                              variant="outline-danger"
                              size="sm"
                              onClick={() => handleDeleteProduct(product.id)}
                              title="Delete Product"
                            >
                              <FaTrash />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
          </Card.Body>
        </Card>
        
        {/* Product Statistics */}
        <Row>
          <Col md={4} className="mb-4">
            <Card className="shadow-sm h-100">
              <Card.Body>
                <h5 className="mb-3">Product Statistics</h5>
                <div className="d-flex justify-content-between mb-2">
                  <span>Total Products:</span>
                  <span className="fw-bold">{products.length}</span>
                </div>
                <div className="d-flex justify-content-between mb-2">
                  <span>Available Products:</span>
                  <span className="fw-bold">
                    {products.filter(p => p.status === 'available').length}
                  </span>
                </div>
                <div className="d-flex justify-content-between mb-2">
                  <span>Hidden Products:</span>
                  <span className="fw-bold">
                    {products.filter(p => p.status === 'hidden').length}
                  </span>
                </div>
                <div className="d-flex justify-content-between mb-2">
                  <span>Sold Out Products:</span>
                  <span className="fw-bold">
                    {products.filter(p => p.status === 'sold out').length}
                  </span>
                </div>
                <div className="d-flex justify-content-between mb-2">
                  <span>Featured Products:</span>
                  <span className="fw-bold">
                    {products.filter(p => p.featured).length}
                  </span>
                </div>
                <div className="d-flex justify-content-between">
                  <span>Reported Products:</span>
                  <span className="fw-bold text-danger">
                    {products.filter(p => p.reported).length}
                  </span>
                </div>
              </Card.Body>
            </Card>
          </Col>
          
          <Col md={4} className="mb-4">
            <Card className="shadow-sm h-100">
              <Card.Body>
                <h5 className="mb-3">Category Distribution</h5>
                <div className="table-responsive">
                  <Table>
                    <thead>
                      <tr>
                        <th>Category</th>
                        <th>Products</th>
                      </tr>
                    </thead>
                    <tbody>
                      {categories.map((category, index) => {
                        const count = products.filter(product => product.category === category).length;
                        if (count === 0) return null;
                        
                        return (
                          <tr key={index}>
                            <td>{category}</td>
                            <td>{count}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </div>
              </Card.Body>
            </Card>
          </Col>
          
          <Col md={4} className="mb-4">
            <Card className="shadow-sm h-100">
              <Card.Body>
                <h5 className="mb-3">Top Selling Products</h5>
                {products.length === 0 ? (
                  <p className="text-muted">No products available</p>
                ) : (
                  <div>
                    {[...products]
                      .sort((a, b) => b.sales - a.sales)
                      .slice(0, 5)
                      .map((product, index) => (
                        <div key={product.id} className={index < 4 ? 'mb-3 pb-3 border-bottom' : ''}>
                          <div className="d-flex align-items-center">
                            <div className="me-3">
                              <Badge bg="primary" className="rounded-circle p-2">
                                {index + 1}
                              </Badge>
                            </div>
                            <div>
                              <div className="fw-bold">{product.name}</div>
                              <small className="text-muted">
                                {product.sales} sales · GH₵ {product.price.toFixed(2)}
                              </small>
                            </div>
                          </div>
                        </div>
                      ))
                    }
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
        
        {/* Product Detail Modal */}
        <Modal 
          show={showProductModal} 
          onHide={() => setShowProductModal(false)}
          size="lg"
        >
          <Modal.Header closeButton>
            <Modal.Title>Product Details</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {selectedProduct && (
              <Row>
                <Col md={4}>
                  <img 
                    src={selectedProduct.image} 
                    alt={selectedProduct.name} 
                    className="img-fluid mb-3"
                  />
                  <div className="d-grid gap-2">
                    <Button 
                      as={Link}
                      to={`/products/${selectedProduct.id}`}
                      variant="primary"
                      target="_blank"
                    >
                      View Product Page
                    </Button>
                  </div>
                </Col>
                <Col md={8}>
                  <h4>{selectedProduct.name}</h4>
                  <p className="text-muted">
                    Added on {new Date(selectedProduct.dateAdded).toLocaleDateString()}
                  </p>
                  
                  <Row className="mb-3">
                    <Col sm={6}>
                      <p className="mb-1"><strong>Price:</strong> GH₵ {selectedProduct.price.toFixed(2)}</p>
                      <p className="mb-1"><strong>Category:</strong> {selectedProduct.category}</p>
                      <p className="mb-1"><strong>Condition:</strong> {selectedProduct.condition}</p>
                    </Col>
                    <Col sm={6}>
                      <p className="mb-1"><strong>Stock:</strong> {selectedProduct.stock}</p>
                      <p className="mb-1"><strong>Sales:</strong> {selectedProduct.sales}</p>
                      <p className="mb-1">
                        <strong>Status:</strong>{' '}
                        <Badge 
                          bg={
                            selectedProduct.status === 'available' ? 'success' : 
                            selectedProduct.status === 'hidden' ? 'secondary' : 
                            'danger'
                          }
                        >
                          {selectedProduct.status}
                        </Badge>
                      </p>
                    </Col>
                  </Row>
                  
                  <div className="mb-3">
                    <h5>Seller Information</h5>
                    <p className="mb-1"><strong>Name:</strong> {selectedProduct.seller.name}</p>
                    <p className="mb-1"><strong>Department:</strong> {selectedProduct.seller.department}</p>
                    <Button 
                      as={Link}
                      to={`/admin/users/${selectedProduct.seller.id}`}
                      variant="outline-primary"
                      size="sm"
                    >
                      View Seller Profile
                    </Button>
                  </div>
                  
                  <div className="mb-3">
                    <h5>Product Flags</h5>
                    <div className="d-flex gap-2 mb-2">
                      <Badge 
                        bg={selectedProduct.featured ? 'success' : 'secondary'}
                        className="p-2"
                      >
                        {selectedProduct.featured ? 'Featured' : 'Not Featured'}
                      </Badge>
                      <Badge 
                        bg={selectedProduct.reported ? 'danger' : 'success'}
                        className="p-2"
                      >
                        {selectedProduct.reported ? 'Reported' : 'No Reports'}
                      </Badge>
                    </div>
                    <div className="d-flex gap-2">
                      <Button 
                        variant={selectedProduct.featured ? 'outline-secondary' : 'outline-success'}
                        size="sm"
                        onClick={() => {
                          handleToggleFeatured(selectedProduct.id);
                          setShowProductModal(false);
                        }}
                      >
                        {selectedProduct.featured ? 'Remove from Featured' : 'Add to Featured'}
                      </Button>
                      {selectedProduct.reported && (
                        <Button 
                          variant="outline-danger"
                          size="sm"
                          onClick={() => {
                            handleClearReport(selectedProduct.id);
                            setShowProductModal(false);
                          }}
                        >
                          Clear Report
                        </Button>
                      )}
                    </div>
                  </div>
                </Col>
              </Row>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowProductModal(false)}>
              Close
            </Button>
            {selectedProduct && (
              <>
                <Button 
                  variant={selectedProduct.status === 'available' ? 'warning' : 
                           selectedProduct.status === 'suspended' ? 'danger' : 'success'}
                  onClick={() => {
                    handleToggleStatus(selectedProduct.id);
                    setShowProductModal(false);
                  }}
                  disabled={selectedProduct.status === 'sold out'}
                >
                  {selectedProduct.status === 'available' ? 'Suspend Product' : 
                   selectedProduct.status === 'suspended' ? 'Hide Product' : 'Show Product'}
                </Button>
                <Button 
                  variant="danger"
                  onClick={() => {
                    if (window.confirm('Are you sure you want to delete this product?')) {
                      handleDeleteProduct(selectedProduct.id);
                      setShowProductModal(false);
                    }
                  }}
                >
                  Delete Product
                </Button>
              </>
            )}
          </Modal.Footer>
        </Modal>
      </div>
    </>
  );
};

export default AdminProducts;
